import React, { useState, useEffect, useRef } from 'react';
import { firebaseService } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { Unauthorized } from './Unauthorized';
import { 
  Calendar, Camera, IndianRupee, FileText, Activity, 
  MapPin, CheckCircle, Image as ImageIcon, Download,
  User, Lock, Send, ChevronRight, ChevronDown, Folder, 
  File, Plus, X, Maximize2, ExternalLink, ShieldCheck, Mail, Phone, MessageSquare,
  Bell, Trash2, FileUp, Eye, AlertTriangle, CreditCard, Clock
} from 'lucide-react';
import { ResponsiveContainer, Cell, PieChart, Pie, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

export default function ClientDashboard() {
  const { logout, currentUser } = useAuth();
  
  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [costs, setCosts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('progress');
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  // Notifications Dropdown
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [viewAllNotifications, setViewAllNotifications] = useState(false);
  const [showAlarmBanner, setShowAlarmBanner] = useState(true);
  const [paymentStages, setPaymentStages] = useState([]);

  // Photo gallery and lightbox states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [activeLightboxPhoto, setActiveLightboxPhoto] = useState(null);

  // Live Camera states
  const [photoUploadMethod, setPhotoUploadMethod] = useState('file'); // 'file' or 'camera'
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);

  // Document Upload States
  const [showDocModal, setShowDocModal] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState('2D Drawings');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Profile forms states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Contact Contractor form states
  const [inquirySubject, setInquirySubject] = useState('');
  const [inquiryCategory, setInquiryCategory] = useState('General Query');
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState('');

  // Picture Request System states
  const [pictureRequests, setPictureRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestResponses, setRequestResponses] = useState([]);
  const [reqTitle, setReqTitle] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqPriority, setReqPriority] = useState('Normal');
  const [reqArea, setReqArea] = useState('Kitchen');
  const [reqCustomArea, setReqCustomArea] = useState('');
  const [reqSpecialNote, setReqSpecialNote] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [contractorUpdates, setContractorUpdates] = useState([]);
  const [photoViewerData, setPhotoViewerData] = useState(null);
  const [viewerSliderIndex, setViewerSliderIndex] = useState(0);
  const [viewerZoom, setViewerZoom] = useState(1);

  // Document tree expand/collapse states
  const [expandedFolders, setExpandedFolders] = useState({
    '2D Drawings': true,
    '3D Designs': true,
    'Contracts': true,
    'Bills / Invoices': true,
    'PDFs': true,
    'Images': true,
    'Other project files': true
  });

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const startCamera = async (facing = cameraFacingMode) => {
    setCameraError('');
    setCapturedImage(null);
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing }
      });
      setCameraStream(stream);
      setCameraActive(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setCameraError("Could not access camera. Please check permissions.");
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraActive(false);
  };

  const switchCamera = () => {
    const nextFacing = cameraFacingMode === 'environment' ? 'user' : 'environment';
    setCameraFacingMode(nextFacing);
    if (cameraActive) {
      startCamera(nextFacing);
    }
  };

  const capturePhoto = (vRef) => {
    if (!vRef || !vRef.current) return;
    const video = vRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      if (cameraFacingMode === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setCapturedImage(dataUrl);
      stopCamera();
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  // Close camera on modal close
  useEffect(() => {
    if (!showPhotoModal) {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      setCameraActive(false);
      setCameraStream(null);
      setPhotoUploadMethod('file');
      setCapturedImage(null);
      setCameraError('');
    }
  }, [showPhotoModal]);

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);


  const loadProjectData = async () => {
    try {
      setLoading(true);
      const userId = currentUser.uid || currentUser.id;
      const clientProjects = await firebaseService.getProjects(userId, 'client');
      
      if (clientProjects.length === 0) {
        setProject(null);
        setLoading(false);
        return;
      }

      const proj = clientProjects[0]; // assume one project for simplicity
      
      // Strict Project Isolation Check
      if (proj.clientId !== userId) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      setProject(proj);

      // Fetch related data in parallel
      const [updatesData, timelineData, costsData, documentsData, notificationsData, photosData, stagesData] = await Promise.all([
        firebaseService.getDailyUpdates(proj.id),
        firebaseService.getTimeline(proj.id),
        firebaseService.getCosts(proj.id),
        firebaseService.getDocuments(proj.id),
        firebaseService.getNotifications(userId, 'client'),
        firebaseService.getProgressPhotos(proj.id),
        firebaseService.getPaymentStages(proj.id)
      ]);

      setUpdates(updatesData);
      setTimeline(timelineData);
      setCosts(costsData);
      setDocuments(documentsData);
      setNotifications(notificationsData);
      setProgressPhotos(photosData);
      setPaymentStages(stagesData);

    } catch (err) {
      console.error("Error loading client project data: ", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadProjectData();
    }
  }, [currentUser]);

  // Realtime subscription for Notifications, Picture Requests & Proactive Updates
  useEffect(() => {
    if (!currentUser || !project) return;
    
    const userId = currentUser.uid || currentUser.id;
    
    const unsubscribeRequests = firebaseService.subscribePictureRequests(userId, 'client', (data) => {
      setPictureRequests(data);
    });
    
    const unsubscribeNotifications = firebaseService.subscribeNotifications(userId, 'client', (data) => {
      setNotifications(data);
    });

    const unsubscribeContractorUpdates = firebaseService.subscribeContractorUpdates(project.id, (data) => {
      setContractorUpdates(data);
    });
    
    return () => {
      unsubscribeRequests();
      unsubscribeNotifications();
      unsubscribeContractorUpdates();
    };
  }, [currentUser, project]);

  // Realtime subscription for selected picture request photos
  useEffect(() => {
    if (!selectedRequest) {
      setRequestResponses([]);
      return;
    }
    
    const unsubscribeResponses = firebaseService.subscribeRequestResponses(selectedRequest.id, (data) => {
      setRequestResponses(data);
    });
    
    return () => unsubscribeResponses();
  }, [selectedRequest]);

  const handleSendPhotoRequest = async (e) => {
    e.preventDefault();
    if (!reqTitle.trim() || !reqDesc.trim()) return;

    setIsSubmittingRequest(true);
    setRequestSuccess(false);

    try {
      const selectedArea = reqArea === 'Custom Area' ? reqCustomArea : reqArea;
      
      const newRequest = {
        projectId: project.id,
        clientId: currentUser.uid || currentUser.id,
        contractorId: project.contractorId,
        title: reqTitle,
        description: reqDesc,
        area: selectedArea,
        priority: reqPriority,
        specialNote: reqSpecialNote,
        status: 'Pending'
      };

      const requestId = await firebaseService.addPictureRequest(newRequest);

      // Trigger realtime notification to Contractor
      await firebaseService.addNotification(
        project.id,
        project.contractorId,
        currentUser.uid || currentUser.id,
        'Site Picture Requested',
        `Client ${currentUser.name} requested site pictures — ${selectedArea} Area.`,
        'contractor',
        'normal',
        { type: 'picture_request', requestId }
      );

      setRequestSuccess(true);
      setReqTitle('');
      setReqDesc('');
      setReqPriority('Normal');
      setReqArea('Kitchen');
      setReqCustomArea('');
      setReqSpecialNote('');

      setTimeout(() => setRequestSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to submit picture request:", err);
      alert("Failed to submit request. Please try again.");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleMarkRequestCompleted = async (requestId) => {
    try {
      await firebaseService.updatePictureRequestStatus(requestId, 'Completed');
      
      // Update local request status
      setSelectedRequest(prev => prev && prev.id === requestId ? { ...prev, status: 'Completed' } : prev);

      // Notify Contractor
      await firebaseService.addNotification(
        project.id,
        project.contractorId,
        currentUser.uid || currentUser.id,
        'Picture Request Resolved',
        `Client ${currentUser.name} marked picture request as reviewed and Completed.`,
        'contractor'
      );
      
      alert("Ticket closed and marked as completed!");
    } catch (err) {
      console.error("Error closing picture request:", err);
      alert("Error closing ticket. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-100">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent mx-auto" />
          <p className="text-sm text-slate-400">Loading project data...</p>
        </div>
      </div>
    );
  }

  if (unauthorized || !project) {
    return <Unauthorized />;
  }

  // Calculate project completion percent average
  const projProgressAvg = timeline.length > 0
    ? Math.round(timeline.reduce((sum, t) => sum + t.progress, 0) / timeline.length)
    : 0;

  // Process Cost Data
  const totalBudget = project.budget || 0;
  const totalPaid = costs.filter(c => c.status === 'Paid').reduce((sum, c) => sum + c.amount, 0);
  const totalPending = costs.filter(c => c.status === 'Pending').reduce((sum, c) => sum + c.amount, 0);
  const remainingBudget = totalBudget - totalPaid;

  const categories = costs.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + item.amount;
    return acc;
  }, {});

  const chartData = Object.keys(categories).map(cat => ({
    name: cat,
    value: categories[cat]
  }));

  const comparisonData = [
    { name: 'Allocated Budget', Amount: totalBudget },
    { name: 'Spent to Date', Amount: totalPaid },
    { name: 'Pending Invoices', Amount: totalPending }
  ];

  const COLORS = ['#10b981', '#6366f1', '#0ea5e9', '#f59e0b', '#ec4899'];

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    if (photoUploadMethod === 'file' && !photoFile) return;
    if (photoUploadMethod === 'camera' && !capturedImage) return;

    setUploadingPhoto(true);
    try {
      let base64Url = '';
      if (photoUploadMethod === 'file') {
        base64Url = await convertToBase64(photoFile);
      } else {
        base64Url = capturedImage;
      }
      const photoData = {
        uploadedBy: 'client',
        uploadedByName: currentUser.name,
        photoUrl: base64Url,
        caption: photoCaption || 'Progress image shared by client',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        timestamp: new Date().toISOString()
      };

      await firebaseService.addProgressPhoto(project.id, photoData);

      // Notify Contractor
      await firebaseService.addNotification(
        project.id,
        project.contractorId,
        currentUser.uid || currentUser.id,
        'New Progress Photo Uploaded',
        `Client ${currentUser.name} uploaded a progress photo: "${photoData.caption}".`,
        'contractor'
      );

      // reload
      const freshPhotos = await firebaseService.getProgressPhotos(project.id);
      setProgressPhotos(freshPhotos);
      setShowPhotoModal(false);
      setPhotoFile(null);
      setCapturedImage(null);
      setPhotoCaption('');
    } catch (err) {
      console.error("Failed to upload photo:", err);
      alert("Failed to upload image. It may exceed size limitations.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDocUpload = async (e) => {
    e.preventDefault();
    if (!docFile) return;

    setUploadingDoc(true);
    try {
      const base64Url = await convertToBase64(docFile);
      const sizeStr = (docFile.size / (1024 * 1024)).toFixed(2) + ' MB';
      const docData = {
        name: docFile.name,
        size: sizeStr,
        type: docType,
        fileUrl: base64Url,
        uploadedBy: 'client',
        uploadedByName: currentUser.name,
        visibleTo: 'both'
      };

      await firebaseService.addDocument(project.id, currentUser.uid || currentUser.id, docData);

      // Notify Contractor
      await firebaseService.addNotification(
        project.id,
        project.contractorId,
        currentUser.uid || currentUser.id,
        'New Document Uploaded',
        `Client ${currentUser.name} uploaded document: "${docFile.name}" under category "${docType}".`,
        'contractor'
      );

      // reload
      const freshDocs = await firebaseService.getDocuments(project.id);
      setDocuments(freshDocs);
      setShowDocModal(false);
      setDocFile(null);
    } catch (err) {
      console.error("Document upload failed:", err);
      alert("Failed to upload document. File may be too large.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId, uploader) => {
    if (uploader !== 'client') {
      alert("You can only delete documents that you uploaded.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await firebaseService.deleteDocument(docId);
        const freshDocs = await firebaseService.getDocuments(project.id);
        setDocuments(freshDocs);
      } catch (err) {
        console.error("Error deleting document:", err);
      }
    }
  };

  const handleDeletePhoto = async (photoId, uploader) => {
    if (uploader !== 'client') {
      alert("You can only delete progress photos that you uploaded.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this photo log?")) {
      try {
        await firebaseService.deleteProgressPhoto(photoId);
        const freshPhotos = await firebaseService.getProgressPhotos(project.id);
        setProgressPhotos(freshPhotos);
      } catch (err) {
        console.error("Error deleting photo:", err);
      }
    }
  };

  const handleMarkNotificationRead = async (notifId) => {
    try {
      await firebaseService.markNotificationRead(notifId);
      const userId = currentUser.uid || currentUser.id;
      const freshNotifs = await firebaseService.getNotifications(userId, 'client');
      setNotifications(freshNotifs);
    } catch (err) {
      console.error("Error reading notification:", err);
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.read) {
      await handleMarkNotificationRead(n.id);
    }
    
    if (n.type === 'picture_request' && n.requestId) {
      const req = pictureRequests.find(r => r.id === n.requestId);
      if (req) {
        setSelectedRequest(req);
        setActiveTab('request-picture');
        
        try {
          const resps = await firebaseService.getRequestResponses(req.id);
          if (resps.length > 0) {
            setPhotoViewerData({
              id: req.id,
              requestId: req.id,
              title: req.title,
              area: req.area,
              priority: req.priority,
              caption: resps[0].caption || resps[0].note || 'Requested site photo',
              imageUrls: resps[0].imageUrls,
              uploadedAt: resps[0].uploadedAt,
              status: req.status
            });
            setViewerSliderIndex(0);
            setViewerZoom(1);
          }
        } catch (e) {
          console.error("Error fetching responses for notification request:", e);
        }
      } else {
        try {
          const allReqs = await firebaseService.getPictureRequests(currentUser.uid || currentUser.id, 'client');
          const found = allReqs.find(r => r.id === n.requestId);
          if (found) {
            setSelectedRequest(found);
            setActiveTab('request-picture');
            const resps = await firebaseService.getRequestResponses(found.id);
            if (resps.length > 0) {
              setPhotoViewerData({
                id: found.id,
                requestId: found.id,
                title: found.title,
                area: found.area,
                priority: found.priority,
                caption: resps[0].caption || resps[0].note || 'Requested site photo',
                imageUrls: resps[0].imageUrls,
                uploadedAt: resps[0].uploadedAt,
                status: found.status
              });
              setViewerSliderIndex(0);
              setViewerZoom(1);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    } else if (n.type === 'contractor_update' && n.updateId) {
      const upd = contractorUpdates.find(u => u.id === n.updateId);
      if (upd) {
        setPhotoViewerData({
          id: upd.id,
          title: 'Contractor Progress Update',
          category: upd.category,
          caption: upd.caption,
          imageUrls: upd.imageUrls,
          uploadedAt: upd.createdAt
        });
        setViewerSliderIndex(0);
        setViewerZoom(1);
        setActiveTab('photos');
      } else {
        try {
          const allUpds = await firebaseService.getContractorUpdates(project.id);
          const found = allUpds.find(u => u.id === n.updateId);
          if (found) {
            setPhotoViewerData({
              id: found.id,
              title: 'Contractor Progress Update',
              category: found.category,
              caption: found.caption,
              imageUrls: found.imageUrls,
              uploadedAt: found.createdAt
            });
            setViewerSliderIndex(0);
            setViewerZoom(1);
            setActiveTab('photos');
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unread = notifications.filter(n => !n.read);
      await Promise.all(unread.map(n => firebaseService.markNotificationRead(n.id)));
      const userId = currentUser.uid || currentUser.id;
      const freshNotifs = await firebaseService.getNotifications(userId, 'client');
      setNotifications(freshNotifs);
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    try {
      await firebaseService.changePassword(currentUser.uid || currentUser.id, newPassword);
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.message || 'Error updating password.');
    }
  };

  const handleContactContractor = async (e) => {
    e.preventDefault();
    setInquiryError('');
    setInquirySuccess(false);

    if (!inquirySubject.trim() || !inquiryMessage.trim()) {
      setInquiryError('Subject and message are required.');
      return;
    }

    try {
      await firebaseService.addNotification(
        project.id,
        project.contractorId,
        currentUser.uid || currentUser.id,
        `Client Inquiry: ${inquirySubject}`,
        `Category: ${inquiryCategory} | Message: ${inquiryMessage}`,
        'contractor'
      );
      
      setInquirySuccess(true);
      setInquirySubject('');
      setInquiryMessage('');
    } catch (err) {
      setInquiryError('Failed to send message. Please try again.');
    }
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderName]: !prev[folderName]
    }));
  };

  // Group documents by the 7 requested folders
  const documentTree = {
    '2D Drawings': documents.filter(d => d.type === '2D Drawings'),
    '3D Designs': documents.filter(d => d.type === '3D Designs'),
    'Contracts': documents.filter(d => d.type === 'Contracts'),
    'Bills / Invoices': documents.filter(d => d.type === 'Bills / Invoices'),
    'PDFs': documents.filter(d => d.type === 'PDFs'),
    'Images': documents.filter(d => d.type === 'Images'),
    'Other project files': documents.filter(d => 
      d.type === 'Other project files' || 
      !['2D Drawings', '3D Designs', 'Contracts', 'Bills / Invoices', 'PDFs', 'Images', 'Other project files'].includes(d.type)
    )
  };

  const triggerDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      alert("No file data attached with this document.");
      return;
    }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const combinedPhotosFeed = [
    ...progressPhotos.map(p => ({
      ...p,
      feedType: 'progress_photo',
      sortTimestamp: p.timestamp || p.date + 'T' + p.time
    })),
    ...contractorUpdates.map(u => ({
      ...u,
      feedType: 'contractor_update',
      uploadedBy: 'contractor',
      uploadedByName: 'BuildSmart Solutions LLC',
      photoUrl: u.imageUrls?.[0] || '',
      sortTimestamp: u.createdAt
    }))
  ].sort((a, b) => b.sortTimestamp.localeCompare(a.sortTimestamp));

  const combinedActivityFeed = [
    ...updates.map(u => ({
      ...u,
      feedType: 'daily_log',
      sortTimestamp: u.date + 'T00:00:00.000Z'
    })),
    ...contractorUpdates.map(u => ({
      ...u,
      feedType: 'contractor_update',
      sortTimestamp: u.createdAt
    }))
  ].sort((a, b) => b.sortTimestamp.localeCompare(a.sortTimestamp));

  return (
    <div className="min-h-screen bg-slate-950 dark:bg-slate-950 text-slate-100 pb-16 transition-colors duration-300 relative">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/80 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-450 border border-emerald-500/20">
                <CheckCircle className="h-5 w-5 animate-pulse-soft" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-none">ApexBuild</h1>
                <span className="text-[10px] uppercase font-semibold tracking-wider text-emerald-450">Client Portal</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-white">{currentUser.name}</p>
                <p className="text-xs text-slate-400">{currentUser.email}</p>
              </div>

              {/* Sleek Notification Bell with Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowNotificationsDropdown(!showNotificationsDropdown);
                    if (!showNotificationsDropdown) {
                      setViewAllNotifications(false);
                    }
                  }}
                  className={`p-2 rounded-xl border transition-all relative ${
                    showNotificationsDropdown 
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                      : 'border-slate-800 bg-slate-900 text-slate-400 hover:text-white hover:border-slate-700'
                  }`}
                  title="Notifications"
                >
                  <Bell className="h-4.5 w-4.5" />
                  {notifications.some(n => !n.read) && (
                    <span className={`absolute top-1.5 right-1.5 h-2 w-2 rounded-full ring-2 ring-slate-900 animate-pulse-soft ${
                      notifications.some(n => !n.read && n.priority === 'important') ? 'bg-rose-500' : 'bg-emerald-500'
                    }`} />
                  )}
                </button>

                {showNotificationsDropdown && (
                  <>
                    {/* Invisible overlay to close dropdown on click outside */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => {
                        setShowNotificationsDropdown(false);
                        setViewAllNotifications(false);
                      }} 
                    />
                    
                    {/* Dropdown panel */}
                    <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-xl border border-slate-800 bg-slate-900/95 p-4 shadow-2xl backdrop-blur-md z-50 animate-dropdown space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-850 pb-3">
                        <div className="flex items-center gap-2">
                          <Bell className="h-4 w-4 text-emerald-400" />
                          <h3 className="font-bold text-white text-xs sm:text-sm">Project Alerts</h3>
                        </div>
                        {notifications.some(n => !n.read) && (
                          <button 
                            onClick={handleMarkAllRead}
                            className="text-[10px] text-emerald-450 hover:underline font-bold"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                        {notifications.length === 0 ? (
                          <div className="text-center py-8 text-slate-500 text-xs">
                            Your inbox is completely clear!
                          </div>
                        ) : (
                          (() => {
                            const importantNotifs = notifications.filter(n => n.priority === 'important');
                            const normalNotifs = notifications.filter(n => n.priority !== 'important');
                            const sortedNotifs = [...importantNotifs, ...normalNotifs];
                            const visibleNotifs = viewAllNotifications ? sortedNotifs : sortedNotifs.slice(0, 5);
                            
                            return visibleNotifs.map((n) => (
                              <div
                                key={n.id}
                                onClick={() => {
                                  handleNotificationClick(n);
                                }}
                                className={`rounded-lg border p-3 flex flex-col gap-1 transition-all cursor-pointer relative hover:border-slate-800 ${
                                  n.read
                                    ? 'border-slate-850 bg-slate-950/20 text-slate-450'
                                    : n.priority === 'important'
                                      ? 'border-rose-500/30 bg-rose-500/5 text-slate-200 shadow-sm'
                                      : 'border-emerald-500/20 bg-emerald-500/5 text-slate-200 shadow-sm'
                                }`}
                              >
                                <div className="flex justify-between items-start gap-2">
                                  <h4 className="font-bold text-xs text-white flex items-center gap-1">
                                    {n.priority === 'important' && (
                                      <AlertTriangle className="h-3.5 w-3.5 text-rose-500 shrink-0 animate-pulse" />
                                    )}
                                    {n.title}
                                    {n.priority === 'important' && (
                                      <span className="bg-rose-500/20 text-rose-455 text-[8px] font-extrabold px-1 rounded">IMPORTANT</span>
                                    )}
                                  </h4>
                                  {!n.read && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkNotificationRead(n.id);
                                      }}
                                      className={`text-[9px] font-bold hover:underline shrink-0 ${
                                        n.priority === 'important' ? 'text-rose-400' : 'text-emerald-455'
                                      }`}
                                    >
                                      Mark read
                                    </button>
                                  )}
                                </div>
                                <p className="text-[11px] leading-normal text-slate-350">{n.message}</p>
                                <span className="text-[9px] text-slate-555 block font-mono mt-0.5">{n.date}</span>
                              </div>
                            ));
                          })()
                        )}
                      </div>

                      <div className="flex justify-between items-center border-t border-slate-850 pt-3 text-[10px]">
                        {notifications.length > 5 && !viewAllNotifications ? (
                          <button
                            onClick={() => setViewAllNotifications(true)}
                            className="font-bold text-sky-400 hover:text-sky-300 hover:underline"
                          >
                            View All Notifications
                          </button>
                        ) : (
                          <span className="text-slate-500 font-semibold uppercase tracking-wider">All caught up</span>
                        )}
                        
                        <button
                          onClick={() => {
                            setShowNotificationsDropdown(false);
                            setViewAllNotifications(false);
                          }}
                          className="font-bold text-slate-455 hover:text-white"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={logout}
                className="rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 px-4 py-2 text-xs font-bold text-slate-300 hover:text-white transition-all shadow-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Top Critical Alerts Banner */}
      {notifications.some(n => !n.read && n.priority === 'important') && showAlarmBanner && (
        <div className="bg-rose-500/10 border-b border-rose-500/20 text-rose-200 px-4 py-3 shadow-sm">
          <div className="mx-auto max-w-7xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs">
              <AlertTriangle className="h-4.5 w-4.5 text-rose-500 shrink-0 animate-pulse" />
              <span>
                <strong>CRITICAL ALERTS DETECTED:</strong> There are outstanding payment milestones or critical items requiring your immediate review.
              </span>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <button 
                onClick={() => {
                  setShowNotificationsDropdown(true);
                  setViewAllNotifications(true);
                }} 
                className="text-xs font-bold text-rose-400 hover:underline"
              >
                View Alerts
              </button>
              <button 
                onClick={() => setShowAlarmBanner(false)}
                className="text-slate-450 hover:text-white text-xs font-extrabold"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
        
        {/* Project Hero Section */}
        <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-6 sm:p-8 shadow-xl">
          <div className="absolute top-0 right-0 -z-10 h-72 w-72 rounded-full bg-emerald-500/5 blur-[50px]" />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            
            <div className="space-y-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-400 border border-emerald-500/20">
                <MapPin className="h-3.5 w-3.5" /> Site Location Active
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">{project.projectName}</h2>
              <p className="text-sm text-slate-400 max-w-xl">
                Location: {project.siteLocation}
              </p>
              <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-1">
                <span>Start Date: {project.startDate}</span>
                <span>•</span>
                <span>Est. Completion: {project.estimatedFinish || project.deadline}</span>
                <span>•</span>
                <span>Type: {project.constructionType}</span>
              </div>
            </div>

            {/* Progress Gauge */}
            <div className="flex items-center gap-5 bg-slate-950/40 border border-slate-900 rounded-2xl p-5 md:w-80 shrink-0">
              <div className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-slate-800">
                {/* Visual completion ring */}
                <svg className="absolute -rotate-90 h-20 w-20">
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="transparent"
                    className="text-slate-800"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 34}
                    strokeDashoffset={2 * Math.PI * 34 * (1 - projProgressAvg / 100)}
                    className="text-emerald-500 transition-all duration-1000"
                  />
                </svg>
                <span className="text-sm font-extrabold text-white font-mono">{projProgressAvg}%</span>
              </div>
              <div>
                <h4 className="font-bold text-white text-sm">Overall Progress</h4>
                <p className="text-[11px] text-slate-400 mt-1 leading-normal">Checklist completion score across current project scope.</p>
              </div>
            </div>

          </div>
        </section>

        {/* Tab Navigation Menu */}
        <section className="space-y-6">
          <div className="flex border-b border-slate-850 overflow-x-auto no-scrollbar">
            {[
              { id: 'progress', label: 'Daily Logs', icon: Activity },
              { id: 'photos', label: 'Timeline Photos', icon: Camera },
              { id: 'request-picture', label: 'Picture Requests', icon: Camera },
              { id: 'timeline', label: 'Timeline Milestones', icon: Calendar },
              { id: 'costs', label: 'Costs & Ledger', icon: IndianRupee },
              { id: 'documents', label: 'Documents Vault', icon: FileText },
              { id: 'payments', label: 'Payment Stages', icon: CreditCard },
              { id: 'profile', label: 'Inquiries & Profile', icon: User }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                    isActive 
                      ? 'border-emerald-500 text-emerald-450 font-bold bg-emerald-500/5' 
                      : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Render Tab Contents */}
          <div className="min-h-[300px]">
            
            {/* Request Picture Tab */}
            {activeTab === 'request-picture' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="border-b border-slate-900 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-md font-bold text-white">Request Construction Photos</h3>
                    <p className="text-xs text-slate-450 mt-0.5">Request live construction site photos from your contractor for verification</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {/* Left: Send Request Form */}
                  <div className="lg:col-span-5">
                    <div className="glass-panel rounded-2xl p-6 border border-slate-800 bg-slate-900/40 space-y-4">
                      <div className="flex items-center gap-2 border-b border-slate-850 pb-3">
                        <Camera className="h-5 w-5 text-emerald-450" />
                        <h4 className="font-bold text-white text-sm">Create Photo Request</h4>
                      </div>

                      <form onSubmit={handleSendPhotoRequest} className="space-y-4">
                        {requestSuccess && (
                          <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs font-semibold text-emerald-400">
                            <CheckCircle className="h-4 w-4 shrink-0" />
                            <span>Request sent successfully! Your builder was notified.</span>
                          </div>
                        )}

                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Request Title</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. Kitchen ceiling framing"
                            value={reqTitle}
                            onChange={(e) => setReqTitle(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Request Description</label>
                          <textarea
                            rows={3}
                            required
                            placeholder="Detail what you need to see, e.g. Need updated kitchen ceiling work photos to check electrical wire routing..."
                            value={reqDesc}
                            onChange={(e) => setReqDesc(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Priority Level</label>
                            <select
                              value={reqPriority}
                              onChange={(e) => setReqPriority(e.target.value)}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                            >
                              <option value="Normal">Normal</option>
                              <option value="Urgent">Urgent</option>
                              <option value="High Priority">High Priority</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Area Selection</label>
                            <select
                              value={reqArea}
                              onChange={(e) => setReqArea(e.target.value)}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                            >
                              <option value="Kitchen">Kitchen</option>
                              <option value="Bedroom">Bedroom</option>
                              <option value="Living Room">Living Room</option>
                              <option value="Bathroom">Bathroom</option>
                              <option value="Roof">Roof</option>
                              <option value="Foundation">Foundation</option>
                              <option value="Exterior">Exterior</option>
                              <option value="Site Entrance">Site Entrance</option>
                              <option value="Custom Area">Custom Area</option>
                            </select>
                          </div>
                        </div>

                        {reqArea === 'Custom Area' && (
                          <div className="animate-slideDown">
                            <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Custom Area Name</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Basement stairs"
                              value={reqCustomArea}
                              onChange={(e) => setReqCustomArea(e.target.value)}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Special Note</label>
                          <textarea
                            rows={2}
                            placeholder="e.g. Ensure electrical wires are visible in the photo."
                            value={reqSpecialNote}
                            onChange={(e) => setReqSpecialNote(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={isSubmittingRequest}
                            className="flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
                          >
                            <Send className="h-3.5 w-3.5" /> {isSubmittingRequest ? 'Sending...' : 'SEND REQUEST'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Right: Request History and Details */}
                  <div className="lg:col-span-7 space-y-6">
                    <div className="glass-panel rounded-2xl p-6 border border-slate-800 bg-slate-900/40">
                      <div className="border-b border-slate-850 pb-3 mb-4">
                        <h4 className="font-bold text-white text-sm">Requests History</h4>
                      </div>

                      {pictureRequests.length === 0 ? (
                        <div className="text-center py-12 text-slate-500 text-xs">
                          No picture requests submitted yet.
                        </div>
                      ) : (
                        <div className="space-y-3.5 max-h-[480px] overflow-y-auto pr-1">
                          {pictureRequests.map((req) => {
                            const isSelected = selectedRequest?.id === req.id;
                            return (
                              <div
                                key={req.id}
                                onClick={() => setSelectedRequest(req)}
                                className={`rounded-xl border p-4 transition-all cursor-pointer ${
                                  isSelected
                                    ? 'border-emerald-500 bg-emerald-500/5'
                                    : 'border-slate-800 bg-slate-950/20 hover:border-slate-750'
                                }`}
                              >
                                <div className="flex justify-between items-start gap-2">
                                  <div>
                                    <h5 className="font-bold text-xs text-white">{req.title}</h5>
                                    <p className="text-[10px] text-slate-400 mt-0.5">Area: {req.area}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-1.5">
                                    <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                      req.priority === 'High Priority' || req.priority === 'Urgent'
                                        ? 'bg-rose-500/10 text-rose-455 border border-rose-500/20'
                                        : 'bg-slate-800 text-slate-300'
                                    }`}>
                                      {req.priority}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                      req.status === 'Completed'
                                        ? 'bg-emerald-500/10 text-emerald-450 border border-emerald-500/20'
                                        : req.status === 'Photo Uploaded'
                                          ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20'
                                          : req.status === 'Viewed'
                                            ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                                            : 'bg-amber-500/10 text-amber-455 border border-amber-500/20'
                                    }`}>
                                      {req.status}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-[11px] text-slate-350 line-clamp-1 mt-2">{req.description}</p>
                                <span className="text-[9px] text-slate-555 block font-mono mt-1.5">{new Date(req.createdAt).toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Active Selected Request Timeline & Photo Responses */}
                    {selectedRequest && (
                      <div className="glass-panel rounded-2xl p-6 border border-slate-800 bg-slate-900/40 space-y-6 animate-slideDown">
                        <div className="flex justify-between items-center border-b border-slate-850 pb-3">
                          <div>
                            <h4 className="font-bold text-white text-sm">{selectedRequest.title}</h4>
                            <p className="text-[10px] text-slate-400">Detailed Status Timeline</p>
                          </div>
                          <button
                            onClick={() => setSelectedRequest(null)}
                            className="text-xs font-bold text-slate-455 hover:text-white"
                          >
                            Close Details
                          </button>
                        </div>

                        {/* Request Timeline */}
                        <div className="grid grid-cols-5 gap-2 relative py-4">
                          {/* Progress Line */}
                          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-800 -translate-y-1/2 z-0" />
                          {(() => {
                            const steps = [
                              { key: 'Pending', label: 'Pending' },
                              { key: 'Viewed', label: 'Viewed' },
                              { key: 'In Progress', label: 'In Progress' },
                              { key: 'Pictures Sent', label: 'Pictures Sent' },
                              { key: 'Completed', label: 'Completed' }
                            ];
                            const currentIdx = steps.findIndex(s => s.key === selectedRequest.status);
                            
                            return steps.map((step, idx) => {
                              const isDone = idx <= currentIdx;
                              const isCurrent = idx === currentIdx;
                              return (
                                <div key={step.key} className="flex flex-col items-center z-10 text-center space-y-2">
                                  <div className={`h-6 w-6 rounded-full flex items-center justify-center border text-[10px] font-bold ${
                                    isDone
                                      ? 'bg-emerald-500 border-emerald-500 text-white'
                                      : 'bg-slate-950 border-slate-800 text-slate-500'
                                  } ${isCurrent ? 'ring-4 ring-emerald-500/20 scale-110' : ''}`}>
                                    {idx + 1}
                                  </div>
                                  <span className={`text-[9px] font-bold ${isDone ? 'text-white' : 'text-slate-500'}`}>
                                    {step.label}
                                  </span>
                                </div>
                              );
                            });
                          })()}
                        </div>

                        {/* Description Details */}
                        <div className="bg-slate-950/40 p-4 rounded-xl border border-slate-850 text-xs space-y-2">
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase block">Description</span>
                            <span className="text-slate-300 mt-1 block leading-relaxed">{selectedRequest.description}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-900/50 text-[10px] text-slate-400">
                            <span>Area: <strong className="text-slate-200">{selectedRequest.area}</strong></span>
                            <span>Priority: <strong className="text-slate-200">{selectedRequest.priority}</strong></span>
                          </div>
                        </div>
                        {/* Photo Responses Details */}
                        {requestResponses.length > 0 ? (
                          <div className="space-y-4">
                            <div className="border-t border-slate-850 pt-4">
                              <h5 className="text-xs font-bold text-white mb-3">Photos Received</h5>
                              {requestResponses.map((resp) => (
                                <div key={resp.id} className="space-y-3">
                                  <div className="flex justify-between items-center text-[10px] text-slate-455">
                                    <span>Uploaded By: <strong className="text-slate-200">Contractor</strong></span>
                                    <span>Time: {new Date(resp.uploadedAt).toLocaleString()}</span>
                                  </div>

                                  {/* Photo Carousel Slider */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {resp.imageUrls.map((url, imgIdx) => (
                                      <div
                                        key={imgIdx}
                                        onClick={() => {
                                          setPhotoViewerData({
                                            id: selectedRequest.id,
                                            requestId: selectedRequest.id,
                                            title: selectedRequest.title,
                                            area: selectedRequest.area,
                                            priority: selectedRequest.priority,
                                            caption: resp.caption || resp.note || 'Requested site photo',
                                            imageUrls: resp.imageUrls,
                                            uploadedAt: resp.uploadedAt,
                                            status: selectedRequest.status
                                          });
                                          setViewerSliderIndex(imgIdx);
                                          setViewerZoom(1);
                                        }}
                                        className="relative aspect-video rounded-xl overflow-hidden border border-slate-800 bg-slate-950 group cursor-pointer"
                                      >
                                        <img src={url} alt="Contractor response" className="h-full w-full object-contain" />
                                        <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                          <Maximize2 className="h-5 w-5 text-white" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {resp.caption && (
                                    <div className="bg-slate-950/20 p-3 rounded-lg border border-slate-850/60 text-xs italic text-slate-355">
                                      "{resp.caption}"
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Mark reviewed action */}
                            {selectedRequest.status === 'Pictures Sent' && (
                              <button
                                onClick={() => handleMarkRequestCompleted(selectedRequest.id)}
                                className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 py-2.5 text-xs font-bold text-white transition-all shadow-md"
                              >
                                <CheckCircle className="h-4 w-4" /> Mark as Reviewed & Close Ticket
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="border-t border-slate-850 pt-4 text-center py-6 text-slate-500 text-xs">
                            {selectedRequest.status === 'Viewed' ? (
                              <span>Contractor has viewed your request and is preparing the photos.</span>
                            ) : (
                              <span>Waiting for contractor response.</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 1. Progress Tab */}
            {activeTab === 'progress' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                  <div>
                    <h3 className="text-md font-bold text-white">Daily Site Updates</h3>
                    <p className="text-xs text-slate-450 mt-0.5">Progress summaries and site details submitted by your contractor</p>
                  </div>
                </div>

                {combinedActivityFeed.length === 0 ? (
                  <div className="glass-panel rounded-xl p-8 text-center text-slate-500">
                    No daily updates or activity have been posted yet.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {combinedActivityFeed.map((item) => {
                      if (item.feedType === 'daily_log') {
                        return (
                          <div key={item.id} className="glass-panel rounded-2xl p-6 shadow-sm space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-900 pb-3.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-md">Daily Log</span>
                                <span className="text-slate-555">•</span>
                                <span className="text-xs font-semibold text-slate-400">{item.date}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-emerald-450 bg-emerald-500/10 px-2.5 py-1 rounded">
                                  Labor Count: {item.labour_count || item.labourCount || 0}
                                </span>
                                {item.materials && (
                                  <span className="text-xs font-semibold text-indigo-400 bg-indigo-400/10 px-2.5 py-1 rounded hidden sm:inline-block max-w-[200px] truncate" title={item.materials}>
                                    Materials: {item.materials}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-sm leading-relaxed text-slate-300">
                              <p className="whitespace-pre-line">{item.notes}</p>
                            </div>

                            {item.photos && item.photos.length > 0 && (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-900">
                                {item.photos.map((ph, index) => (
                                  <div 
                                    key={index} 
                                    className="relative aspect-video rounded-xl overflow-hidden border border-slate-800 hover:border-slate-700 group cursor-pointer"
                                    onClick={() => {
                                      setPhotoViewerData({
                                        id: item.id,
                                        title: 'Daily Log Photo',
                                        caption: 'Daily site update attachment',
                                        imageUrls: item.photos,
                                        uploadedAt: item.date + 'T12:00:00.000Z'
                                      });
                                      setViewerSliderIndex(index);
                                      setViewerZoom(1);
                                    }}
                                  >
                                    <img src={ph} alt="Progress detail" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Maximize2 className="h-5 w-5 text-white drop-shadow" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // contractor_update
                        return (
                          <div key={item.id} className="glass-panel rounded-2xl p-6 shadow-sm border border-emerald-550/10 bg-emerald-950/5 border-emerald-500/10 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-emerald-500/15 pb-3.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-md flex items-center gap-1.5">
                                  <Camera className="h-4.5 w-4.5 text-emerald-400" />
                                  Site Progress Photo Update
                                </span>
                                <span className="text-emerald-800">•</span>
                                <span className="text-xs font-semibold text-slate-400">{new Date(item.createdAt).toLocaleDateString()}</span>
                              </div>
                              <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded">
                                Category: {item.category}
                              </span>
                            </div>

                            <div className="text-sm leading-relaxed text-slate-350">
                              <p className="italic">"{item.caption}"</p>
                            </div>

                            {item.imageUrls && item.imageUrls.length > 0 && (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-emerald-500/10">
                                {item.imageUrls.map((url, index) => (
                                  <div 
                                    key={index} 
                                    className="relative aspect-video rounded-xl overflow-hidden border border-slate-800 hover:border-slate-700 group cursor-pointer"
                                    onClick={() => {
                                      setPhotoViewerData({
                                        id: item.id,
                                        title: 'Contractor Progress Update',
                                        category: item.category,
                                        caption: item.caption,
                                        imageUrls: item.imageUrls,
                                        uploadedAt: item.createdAt
                                      });
                                      setViewerSliderIndex(index);
                                      setViewerZoom(1);
                                    }}
                                  >
                                    <img src={url} alt="Progress detail" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Maximize2 className="h-5 w-5 text-white drop-shadow" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 2. Photos Tab - TIMELINE PHOTOS FEATURE */}
            {activeTab === 'photos' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                  <div>
                    <h3 className="text-md font-bold text-white">Project Photo Timeline</h3>
                    <p className="text-xs text-slate-450 mt-0.5">Chronological feed of progress photos and work update logs from Client and Contractor</p>
                  </div>
                  
                  <button 
                    onClick={() => setShowPhotoModal(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:border-emerald-500 hover:bg-emerald-500/10 px-3.5 py-1.5 text-xs font-semibold text-slate-300 hover:text-emerald-450 transition-all shadow-sm"
                  >
                    <Camera className="h-4 w-4 text-emerald-400" /> Share Progress Photo
                  </button>
                </div>

                {combinedPhotosFeed.length === 0 ? (
                  <div className="glass-panel rounded-xl p-12 text-center text-slate-500 flex flex-col items-center justify-center space-y-3">
                    <Camera className="h-10 w-10 text-slate-600" />
                    <p>No site photos have been logged in the timeline yet.</p>
                  </div>
                ) : (
                  <div className="relative border-l border-slate-800 pl-6 ml-3 space-y-8 py-2">
                    {combinedPhotosFeed.map((ph) => {
                      if (ph.feedType === 'progress_photo') {
                        return (
                          <div key={ph.id} className="relative">
                            {/* Timeline dot indicator */}
                            <span className={`absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                              ph.uploadedBy === 'client' 
                                ? 'bg-emerald-500 border-emerald-500' 
                                : 'bg-indigo-500 border-indigo-500'
                            }`} />
                            
                            <div className="glass-panel rounded-xl p-5 shadow-sm space-y-4 max-w-2xl">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-2">
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Uploaded By</span>
                                  <span className="font-semibold text-white text-xs">{ph.uploadedByName} ({ph.uploadedBy})</span>
                                </div>
                                <div className="text-right text-[10px] text-slate-400">
                                  <span className="block font-mono">Date: {ph.date}</span>
                                  <span className="block font-mono">Time: {ph.time}</span>
                                </div>
                              </div>

                              <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-800 bg-slate-950 group cursor-pointer"
                                   onClick={() => {
                                     setPhotoViewerData({
                                       id: ph.id,
                                       title: 'Timeline Photo Share',
                                       caption: ph.caption,
                                       imageUrls: [ph.photoUrl],
                                       uploadedAt: ph.timestamp || ph.date + 'T' + ph.time
                                     });
                                     setViewerSliderIndex(0);
                                     setViewerZoom(1);
                                   }}>
                                <img src={ph.photoUrl} alt={ph.caption} className="h-full w-full object-contain" />
                                <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Maximize2 className="h-6 w-6 text-white drop-shadow" />
                                </div>
                              </div>

                              <div className="flex items-center justify-between gap-4">
                                <p className="text-xs text-slate-300 italic">"{ph.caption}"</p>
                                
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => triggerDownload(ph.photoUrl, `progress_${ph.date}_${ph.time}.jpg`)}
                                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                                    title="Download Photo"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </button>
                                  {ph.uploadedBy === 'client' && (
                                    <button
                                      onClick={() => handleDeletePhoto(ph.id, ph.uploadedBy)}
                                      className="p-1.5 text-slate-400 hover:text-rose-455 hover:bg-slate-800 rounded transition-colors"
                                      title="Delete Photo Log"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        // feedType === 'contractor_update'
                        return (
                          <div key={ph.id} className="relative">
                            <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 bg-emerald-500 border-emerald-500" />
                            
                            <div className="glass-panel rounded-xl p-5 shadow-sm space-y-4 max-w-2xl">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-2">
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Proactive Work Progress</span>
                                  <span className="font-semibold text-white text-xs">Contractor ({ph.category})</span>
                                </div>
                                <div className="text-right text-[10px] text-slate-400">
                                  <span className="block font-mono">Date: {new Date(ph.createdAt).toLocaleDateString()}</span>
                                  <span className="block font-mono">Time: {new Date(ph.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                </div>
                              </div>

                              <div className={`grid gap-2.5 ${ph.imageUrls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                {ph.imageUrls.map((url, index) => (
                                  <div
                                    key={index}
                                    className="relative aspect-video rounded-xl overflow-hidden border border-slate-800 bg-slate-950 group cursor-pointer"
                                    onClick={() => {
                                      setPhotoViewerData({
                                        id: ph.id,
                                        title: 'Contractor Progress Update',
                                        category: ph.category,
                                        caption: ph.caption,
                                        imageUrls: ph.imageUrls,
                                        uploadedAt: ph.createdAt
                                      });
                                      setViewerSliderIndex(index);
                                      setViewerZoom(1);
                                    }}
                                  >
                                    <img src={url} alt="Update item" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Maximize2 className="h-5 w-5 text-white drop-shadow" />
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="flex items-center justify-between gap-4">
                                <p className="text-xs text-slate-350 italic">"{ph.caption}"</p>
                                
                                <button
                                  onClick={() => triggerDownload(ph.imageUrls[0], `update_${ph.id}.jpg`)}
                                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                                  title="Download Photo"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 3. Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <div className="border-b border-slate-900 pb-3">
                  <h3 className="text-md font-bold text-white">Project Milestone Timeline</h3>
                  <p className="text-xs text-slate-450 mt-0.5">Real-time checklist track of deadlines and task stages</p>
                </div>

                {timeline.length === 0 ? (
                  <div className="glass-panel rounded-xl p-8 text-center text-slate-500">
                    Timeline schedule is not initialized.
                  </div>
                ) : (
                  <div className="relative border-l border-slate-800 pl-6 ml-3 space-y-8 py-2">
                    {timeline.map((item) => (
                      <div key={item.id} className="relative">
                        {/* Milestone dot indicator */}
                        <span className={`absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          item.progress === 100 
                            ? 'bg-emerald-500 border-emerald-500' 
                            : 'bg-slate-955 border-slate-700'
                        }`} />
                        
                        <div className="glass-panel rounded-xl p-5 shadow-sm space-y-3.5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                            <div>
                              <h4 className="font-bold text-white text-sm sm:text-md">{item.task}</h4>
                              <p className="text-[11px] text-slate-400 mt-1">Deadline Plan: {item.deadline}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${
                                item.priority === 'High' 
                                  ? 'bg-rose-500/10 text-rose-455' 
                                  : item.priority === 'Medium' 
                                  ? 'bg-amber-500/10 text-amber-450' 
                                  : 'bg-sky-500/10 text-sky-400'
                              }`}>
                                {item.priority} Priority
                              </span>
                              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${
                                item.progress === 100 
                                  ? 'bg-emerald-500/10 text-emerald-450' 
                                  : item.progress > 0 
                                  ? 'bg-sky-500/10 text-sky-400' 
                                  : 'bg-slate-800 text-slate-400'
                              }`}>
                                {item.status}
                              </span>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-[10px] text-slate-500">
                              <span>Checklist Progress</span>
                              <span className="font-semibold text-slate-300">{item.progress}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-slate-900 overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${item.progress === 100 ? 'bg-emerald-500' : 'bg-sky-500'}`} 
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 4. Costs Tab - INDIAN RUPEE FORMATTING */}
            {activeTab === 'costs' && (
              <div className="space-y-6">
                <div className="border-b border-slate-900 pb-3">
                  <h3 className="text-md font-bold text-white">Financial Sheets & Ledger (INR)</h3>
                  <p className="text-xs text-slate-455 mt-0.5">Category breakdowns, invoices, and budget allocation summaries in Indian Rupees (₹)</p>
                </div>

                {costs.length === 0 ? (
                  <div className="glass-panel rounded-xl p-8 text-center text-slate-500">
                    No budget ledger entries found.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Premium Cost Cards Row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="glass-panel rounded-2xl p-4.5 space-y-1">
                        <span className="text-[10px] font-bold text-slate-450 uppercase tracking-wide">Total Estimate</span>
                        <div className="text-lg sm:text-2xl font-extrabold text-white font-mono">
                          ₹{totalBudget.toLocaleString('en-IN')}
                        </div>
                      </div>
                      
                      <div className="glass-panel rounded-2xl p-4.5 space-y-1 border-l-2 border-l-emerald-500">
                        <span className="text-[10px] font-bold text-slate-450 uppercase tracking-wide">Spent to Date</span>
                        <div className="text-lg sm:text-2xl font-extrabold text-emerald-400 font-mono">
                          ₹{totalPaid.toLocaleString('en-IN')}
                        </div>
                      </div>

                      <div className="glass-panel rounded-2xl p-4.5 space-y-1 border-l-2 border-l-amber-500">
                        <span className="text-[10px] font-bold text-slate-450 uppercase tracking-wide">Pending Invoices</span>
                        <div className="text-lg sm:text-2xl font-extrabold text-amber-400 font-mono">
                          ₹{totalPending.toLocaleString('en-IN')}
                        </div>
                      </div>

                      <div className="glass-panel rounded-2xl p-4.5 space-y-1 border-l-2 border-l-sky-500">
                        <span className="text-[10px] font-bold text-slate-450 uppercase tracking-wide">Remaining Estimate</span>
                        <div className="text-lg sm:text-2xl font-extrabold text-sky-400 font-mono">
                          ₹{remainingBudget.toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      
                      {/* Left: Charts Grid */}
                      <div className="lg:col-span-7 space-y-6">
                        {/* Bar comparison Chart */}
                        <div className="glass-panel rounded-2xl p-5 flex flex-col">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Budget Overview (INR)</h4>
                          <div className="h-56 w-full text-slate-900 font-mono text-xs">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={comparisonData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                <XAxis dataKey="name" stroke="#64748b" />
                                <YAxis stroke="#64748b" />
                                <Tooltip formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Amount']} />
                                <Bar dataKey="Amount" fill="#0ea5e9" radius={[4, 4, 0, 0]}>
                                  <Cell fill="#6366f1" />
                                  <Cell fill="#10b981" />
                                  <Cell fill="#f59e0b" />
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Pie Chart */}
                        <div className="glass-panel rounded-2xl p-5 flex flex-col items-center">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Category Wise Distribution</h4>
                          
                          <div className="h-48 w-full relative">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={chartData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={70}
                                  paddingAngle={4}
                                  dataKey="value"
                                >
                                  {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Amount']} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>

                          {/* Legend list */}
                          <div className="mt-2 flex flex-wrap justify-center gap-3.5">
                            {chartData.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-1.5 text-xs text-slate-300">
                                <span className="h-3 w-3 rounded" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                <span>{item.name}: <strong className="text-white font-mono">₹{item.value.toLocaleString('en-IN')}</strong></span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right: Cost breakdown table */}
                      <div className="lg:col-span-5">
                        <div className="glass-panel rounded-2xl p-5 space-y-4 h-full">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Itemized Cost Log</h4>
                          
                          <div className="overflow-auto max-h-[480px] rounded-xl border border-slate-800">
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="bg-slate-950 border-b border-slate-800 text-slate-450 font-semibold uppercase tracking-wider">
                                  <th className="py-3 px-4">Expense Details</th>
                                  <th className="py-3 px-4">Category</th>
                                  <th className="py-3 px-4">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-850 bg-slate-950/20">
                                {costs.map((c) => (
                                  <tr key={c.id} className="hover:bg-slate-900/10">
                                    <td className="py-3 px-4">
                                      <div className="font-bold text-white">{c.item_name || c.item}</div>
                                      <span className={`inline-block text-[9px] font-bold mt-1 ${
                                        c.status === 'Paid' ? 'text-emerald-450' : 'text-amber-450'
                                      }`}>
                                        {c.status === 'Paid' ? '✓ Paid Invoice' : '⧗ Invoice Pending'}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4 text-slate-450">{c.category}</td>
                                    <td className="py-3 px-4 font-bold text-white font-mono">₹{c.amount.toLocaleString('en-IN')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 5. Documents Tab - FULL MANAGEMENT */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                  <div>
                    <h3 className="text-md font-bold text-white">Blueprint & Document Vault</h3>
                    <p className="text-xs text-slate-450 mt-0.5">Secure folders containing project drawings, permits, designs, and contracts</p>
                  </div>
                  
                  <button
                    onClick={() => setShowDocModal(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:border-emerald-500 hover:bg-emerald-500/10 px-3.5 py-1.5 text-xs font-semibold text-slate-300 hover:text-emerald-450 transition-all shadow-sm"
                  >
                    <FileUp className="h-4 w-4 text-emerald-400" /> Upload Document
                  </button>
                </div>

                {documents.length === 0 ? (
                  <div className="glass-panel rounded-xl p-12 text-center text-slate-550 flex flex-col items-center justify-center space-y-3">
                    <FileText className="h-10 w-10 text-slate-655" />
                    <p>No documents are hosted on this project vault.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(documentTree).map(([folderName, files]) => {
                      const isExpanded = expandedFolders[folderName];
                      return (
                        <div key={folderName} className="glass-panel rounded-2xl overflow-hidden border border-slate-800 shadow-sm">
                          {/* Folder Node */}
                          <button
                            onClick={() => toggleFolder(folderName)}
                            className="w-full flex items-center justify-between p-4 bg-slate-900/30 border-b border-slate-850 hover:bg-slate-900/60 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="h-4.5 w-4.5 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4.5 w-4.5 text-slate-400" />
                              )}
                              <Folder className="h-5 w-5 text-amber-500" />
                              <span className="text-xs sm:text-sm font-bold text-white">{folderName}</span>
                              <span className="text-[10px] text-slate-500 bg-slate-950 px-2 py-0.5 rounded-full">
                                {files.length} {files.length === 1 ? 'file' : 'files'}
                              </span>
                            </div>
                          </button>

                          {/* Files list */}
                          {isExpanded && (
                            <div className="p-2.5 divide-y divide-slate-850 bg-slate-900/10">
                              {files.length === 0 ? (
                                <div className="text-center py-4 text-xs text-slate-550">
                                  No files inside this folder directory.
                                </div>
                              ) : (
                                files.map((doc) => (
                                  <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 gap-3 hover:bg-slate-900/20 rounded-xl transition-colors">
                                    <div className="flex items-center gap-3">
                                      <File className="h-5 w-5 text-sky-400" />
                                      <div>
                                        <h5 className="text-xs font-bold text-white max-w-[200px] sm:max-w-md truncate" title={doc.name}>
                                          {doc.name}
                                        </h5>
                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[9px] text-slate-455 mt-0.5">
                                          <span>Size: {doc.size}</span>
                                          <span>•</span>
                                          <span>Uploaded: {doc.date}</span>
                                          <span>•</span>
                                          <span>By: <strong className="text-slate-300">{doc.uploadedByName || (doc.uploadedBy === 'client' ? 'Client' : 'Contractor')}</strong></span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2 self-end sm:self-center">
                                      <button 
                                        onClick={() => triggerDownload(doc.fileUrl, doc.name)}
                                        className="flex items-center gap-1.5 rounded-lg border border-slate-850 hover:border-slate-700 bg-slate-900/40 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white transition-all"
                                        title={`Download ${doc.name}`}
                                      >
                                        <Download className="h-3.5 w-3.5" /> Download
                                      </button>
                                      {doc.uploadedBy === 'client' && (
                                        <button
                                          onClick={() => handleDeleteDoc(doc.id, doc.uploadedBy)}
                                          className="p-2 text-slate-500 hover:text-rose-455 hover:bg-slate-850 rounded transition-colors"
                                          title="Delete Document"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB: PAYMENT STAGES */}
            {activeTab === 'payments' && (
              <div className="glass-panel rounded-2xl p-6 shadow-sm space-y-6">
                <div className="border-b border-slate-800 pb-4">
                  <h3 className="text-md font-bold text-white">Construction Payment Stages</h3>
                  <p className="text-xs text-slate-450">Track structural completion phases and stage payments</p>
                </div>

                {/* Metrics */}
                {(() => {
                  const totalCost = paymentStages.reduce((sum, s) => sum + s.stageAmount, 0);
                  const totalPaid = paymentStages.reduce((sum, s) => sum + s.paidAmount, 0);
                  const balance = totalCost - totalPaid;
                  const paidPercentage = totalCost > 0 ? Math.round((totalPaid / totalCost) * 100) : 0;

                  return (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                          <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Total Project Cost</span>
                          <h4 className="text-xl font-extrabold text-white mt-1.5 font-mono">₹{totalCost.toLocaleString('en-IN')}</h4>
                          <div className="h-1.5 w-full bg-slate-800 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: '100%' }} />
                          </div>
                        </div>

                        <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                          <span className="text-[10px] uppercase font-bold text-emerald-450 tracking-wider">Total Paid Amount</span>
                          <h4 className="text-xl font-extrabold text-emerald-400 mt-1.5 font-mono">₹{totalPaid.toLocaleString('en-IN')}</h4>
                          <div className="h-1.5 w-full bg-slate-800 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${paidPercentage}%` }} />
                          </div>
                        </div>

                        <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                          <span className="text-[10px] uppercase font-bold text-amber-500 tracking-wider">Remaining Balance</span>
                          <h4 className="text-xl font-extrabold text-amber-400 mt-1.5 font-mono">₹{balance.toLocaleString('en-IN')}</h4>
                          <div className="h-1.5 w-full bg-slate-800 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${100 - paidPercentage}%` }} />
                          </div>
                        </div>
                      </div>

                      {/* Overall Progress Bar */}
                      <div className="bg-slate-900/30 border border-slate-850 p-4 rounded-xl space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400 font-medium">Payment Progress</span>
                          <span className="text-white font-extrabold font-mono">{paidPercentage}% Cleared</span>
                        </div>
                        <div className="h-2.5 w-full bg-slate-950 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500" 
                            style={{ width: `${paidPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Stages List */}
                <div className="overflow-x-auto rounded-xl border border-slate-850">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-950 border-b border-slate-850 text-slate-500 uppercase font-bold tracking-wider">
                        <th className="py-3 px-4">Stage Details</th>
                        <th className="py-3 px-4 text-right">Amount (₹)</th>
                        <th className="py-3 px-4">Due Date</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Paid (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-850 bg-slate-950/15">
                      {paymentStages.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-slate-500 font-semibold">
                            No payment stages registered by your contractor yet.
                          </td>
                        </tr>
                      ) : (
                        paymentStages.map((s, idx) => (
                          <tr key={s.id} className="hover:bg-slate-900/10">
                            <td className="py-3.5 px-4 max-w-xs">
                              <div className="font-extrabold text-white flex items-center gap-1.5">
                                <span className="text-[10px] text-slate-550 font-mono">#{idx+1}</span>
                                {s.stageName}
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{s.stageDescription}</p>
                            </td>
                            <td className="py-3.5 px-4 text-right font-mono font-bold text-white">
                              ₹{s.stageAmount.toLocaleString('en-IN')}
                            </td>
                            <td className="py-3.5 px-4 font-mono text-slate-350">
                              {s.dueDate}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-extrabold uppercase border ${
                                s.status === 'Paid' 
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                  : s.status === 'Overdue'
                                    ? 'bg-rose-500/10 text-rose-455 border-rose-500/20 animate-pulse'
                                    : s.status === 'Partially Paid'
                                      ? 'bg-amber-500/10 text-amber-450 border-amber-500/20'
                                      : 'bg-slate-805 text-slate-400 border-slate-700'
                              }`}>
                                {s.status}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-right font-mono font-bold text-emerald-450">
                              ₹{s.paidAmount.toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 6. Profile & Inquiry Tab */}
            {activeTab === 'profile' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Profile detail card */}
                <div className="lg:col-span-4 space-y-6">
                  <div className="glass-panel rounded-2xl p-6 space-y-5">
                    <div className="flex flex-col items-center text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-450 border border-emerald-500/20 shadow-inner">
                        <User className="h-7 w-7" />
                      </div>
                      <h4 className="font-extrabold text-white text-md mt-3.5">{currentUser.name}</h4>
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-450 mt-1 bg-emerald-500/10 border border-emerald-500/20 px-3 py-0.5 rounded-full">
                        Client Account
                      </span>
                    </div>

                    <div className="border-t border-slate-850 pt-4 space-y-3.5 text-xs text-slate-300">
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-slate-500" />
                        <div>
                          <p className="text-[10px] font-bold text-slate-500 uppercase">Email Address</p>
                          <p className="text-white mt-0.5">{currentUser.email}</p>
                        </div>
                      </div>

                      {currentUser.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-slate-500" />
                          <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase">Contact Number</p>
                            <p className="text-white mt-0.5">{currentUser.phone}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-slate-500" />
                        <div>
                          <p className="text-[10px] font-bold text-slate-500 uppercase">Linked Project Site</p>
                          <p className="text-white mt-0.5 font-medium">{project.projectName}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings Actions: Change Password & Contact Contractor */}
                <div className="lg:col-span-8 space-y-6">
                  
                  {/* Inquiry / Message Contractor Form */}
                  <div className="glass-panel rounded-2xl p-6 space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-850 pb-3">
                      <MessageSquare className="h-5 w-5 text-emerald-450" />
                      <div>
                        <h4 className="font-bold text-white text-sm">Send Contractor Inquiry</h4>
                        <p className="text-[11px] text-slate-450">Submit general queries, order approvals, or design notes</p>
                      </div>
                    </div>

                    <form onSubmit={handleContactContractor} className="space-y-4">
                      {inquirySuccess && (
                        <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3.5 text-xs font-semibold text-emerald-400">
                          <CheckCircle className="h-4 w-4 shrink-0" />
                          <span>Inquiry message sent successfully! Your contractor will receive a notification.</span>
                        </div>
                      )}

                      {inquiryError && (
                        <div className="text-rose-455 text-xs font-semibold bg-rose-500/10 border border-rose-500/20 rounded p-3">
                          {inquiryError}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Inquiry Category</label>
                          <select
                            value={inquiryCategory}
                            onChange={(e) => setInquiryCategory(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          >
                            <option value="General Query">General Query</option>
                            <option value="Design Change">Design Change Approval</option>
                            <option value="Billing / Payment">Invoice Question</option>
                            <option value="Timeline Milestone">Timeline Enquiry</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Subject Heading</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. Cedar planks shade stain"
                            value={inquirySubject}
                            onChange={(e) => setInquirySubject(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Message Content</label>
                        <textarea
                          rows={3}
                          required
                          placeholder="Type inquiry details here for your contractor..."
                          value={inquiryMessage}
                          onChange={(e) => setInquiryMessage(e.target.value)}
                          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white transition-all hover:scale-[1.01]"
                        >
                          <Send className="h-3.5 w-3.5" /> Submit Inquiry
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Change Password Form */}
                  <div className="glass-panel rounded-2xl p-6 space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-850 pb-3">
                      <Lock className="h-5 w-5 text-amber-500" />
                      <div>
                        <h4 className="font-bold text-white text-sm">Modify Account Password</h4>
                        <p className="text-[11px] text-slate-455">Update password security credentials periodically</p>
                      </div>
                    </div>

                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      {passwordSuccess && (
                        <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3.5 text-xs font-semibold text-emerald-450">
                          <ShieldCheck className="h-4 w-4 shrink-0" />
                          <span>Credentials changed successfully. Use your new password at next login!</span>
                        </div>
                      )}

                      {passwordError && (
                        <div className="text-rose-455 text-xs font-semibold bg-rose-500/10 border border-rose-500/20 rounded p-3">
                          {passwordError}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">New Password</label>
                          <input
                            type="password"
                            required
                            placeholder="Min. 6 characters"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] font-bold text-slate-350 uppercase mb-1.5">Confirm Password</label>
                          <input
                            type="password"
                            required
                            placeholder="Repeat new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 px-4 py-2.5 text-xs font-bold text-white transition-all hover:scale-[1.01]"
                        >
                          <Lock className="h-3.5 w-3.5" /> Commit Password
                        </button>
                      </div>
                    </form>
                  </div>

                </div>

              </div>
            )}

          </div>
        </section>

      </main>

      {/* Progress Photo Share Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-bold text-white text-md">Upload Progress Photo</h3>
              <button onClick={() => {
                setShowPhotoModal(false);
                setPhotoFile(null);
                setPhotoCaption('');
              }} className="text-slate-400 hover:text-white">&times;</button>
            </div>

            <form onSubmit={handlePhotoUpload} className="mt-4 space-y-4">
              <p className="text-xs text-slate-400 leading-relaxed">
                Choose an image file or take a photo using your camera. It will upload as a progress milestone visible in the project photo timeline.
              </p>

              {/* Upload Method Tabs */}
              <div className="flex border-b border-slate-800 pb-2 mb-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPhotoUploadMethod('file');
                    stopCamera();
                  }}
                  className={`flex-1 pb-1.5 text-center text-xs font-bold border-b-2 transition-all ${
                    photoUploadMethod === 'file'
                      ? 'border-emerald-500 text-emerald-450'
                      : 'border-transparent text-slate-400 hover:text-slate-350'
                  }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhotoUploadMethod('camera');
                    startCamera();
                  }}
                  className={`flex-1 pb-1.5 text-center text-xs font-bold border-b-2 transition-all ${
                    photoUploadMethod === 'camera'
                      ? 'border-emerald-500 text-emerald-450'
                      : 'border-transparent text-slate-400 hover:text-slate-350'
                  }`}
                >
                  Take Photo
                </button>
              </div>

              {photoUploadMethod === 'file' && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1.5">Select Image File</label>
                  <input 
                    type="file"
                    required
                    accept="image/*"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                    className="w-full text-xs text-slate-300 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-500/10 file:text-emerald-450 hover:file:bg-emerald-500/20 bg-slate-950 border border-slate-700 rounded-md p-1"
                  />
                </div>
              )}

              {photoUploadMethod === 'camera' && (
                <div className="space-y-3">
                  {cameraError && (
                    <div className="text-xs text-rose-455 bg-rose-500/10 p-2.5 rounded-lg border border-rose-500/20">
                      {cameraError}
                    </div>
                  )}

                  {/* Camera Screen */}
                  {!capturedImage ? (
                    <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-750 bg-slate-955 flex flex-col items-center justify-center">
                      {cameraActive ? (
                        <>
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className="h-full w-full object-cover"
                            style={{ transform: cameraFacingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                          />
                          <div className="absolute top-3 left-3 bg-rose-500/80 text-white font-extrabold text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1.5 animate-pulse">
                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                            LIVE
                          </div>
                        </>
                      ) : (
                        <div className="text-center p-6 space-y-3">
                          <Camera className="h-8 w-8 text-slate-500 mx-auto" />
                          <button
                            type="button"
                            onClick={() => startCamera()}
                            className="px-3.5 py-1.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-450 border border-emerald-500/20 text-xs font-bold transition-all"
                          >
                            Enable Camera
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Captured Image Preview */
                    <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-750 bg-slate-955 flex items-center justify-center">
                      <img src={capturedImage} alt="Captured progress" className="h-full w-full object-contain" />
                      <div className="absolute top-3 left-3 bg-emerald-500/85 text-white font-extrabold text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1">
                        PREVIEW
                      </div>
                    </div>
                  )}

                  {/* Camera Controls */}
                  <div className="flex justify-center gap-3">
                    {cameraActive && !capturedImage && (
                      <>
                        <button
                          type="button"
                          onClick={() => capturePhoto(videoRef)}
                          className="flex-1 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                        >
                          <Camera className="h-4 w-4" /> Capture Photo
                        </button>
                        <button
                          type="button"
                          onClick={switchCamera}
                          className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all"
                          title="Switch Camera"
                        >
                          Flip Camera
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-305 text-xs font-bold transition-all"
                        >
                          Stop
                        </button>
                      </>
                    )}
                    {capturedImage && (
                      <button
                        type="button"
                        onClick={retakePhoto}
                        className="flex-1 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-205 text-xs font-bold transition-all"
                      >
                        Retake Photo
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1.5">Photo Caption / Description</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. Wall bricklaying progress"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPhotoModal(false);
                    setPhotoFile(null);
                    setPhotoCaption('');
                  }}
                  className="flex-1 rounded border border-slate-800 bg-slate-900 py-2 text-xs font-bold text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingPhoto || (photoUploadMethod === 'file' ? !photoFile : !capturedImage)}
                  className="flex-1 rounded bg-emerald-500 py-2 text-xs font-bold text-white hover:bg-emerald-600 shadow disabled:opacity-50"
                >
                  {uploadingPhoto ? 'Uploading...' : 'Post Photo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-bold text-white text-md">Upload Document</h3>
              <button onClick={() => {
                setShowDocModal(false);
                setDocFile(null);
              }} className="text-slate-400 hover:text-white">&times;</button>
            </div>

            <form onSubmit={handleDocUpload} className="mt-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1.5">Select File (PDF, Image, etc.)</label>
                <input 
                  type="file"
                  required
                  onChange={(e) => setDocFile(e.target.files[0])}
                  className="w-full text-xs text-slate-300 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-emerald-500/10 file:text-emerald-450 hover:file:bg-emerald-500/20 bg-slate-950 border border-slate-700 rounded-md p-1"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1.5">Category Folder</label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="2D Drawings">2D Drawings</option>
                  <option value="3D Designs">3D Designs</option>
                  <option value="Contracts">Contracts</option>
                  <option value="Bills / Invoices">Bills / Invoices</option>
                  <option value="PDFs">PDFs</option>
                  <option value="Images">Images</option>
                  <option value="Other project files">Other project files</option>
                </select>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDocModal(false);
                    setDocFile(null);
                  }}
                  className="flex-1 rounded border border-slate-800 bg-slate-900 py-2 text-xs font-bold text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingDoc}
                  className="flex-1 rounded bg-emerald-500 py-2 text-xs font-bold text-white hover:bg-emerald-600 shadow disabled:opacity-50"
                >
                  {uploadingDoc ? 'Uploading...' : 'Upload File'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox Modal Overlay */}
      {activeLightboxPhoto && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/95 p-4 backdrop-blur-md">
          {/* Close trigger */}
          <button 
            onClick={() => setActiveLightboxPhoto(null)} 
            className="absolute top-4 right-4 z-55 rounded-full bg-slate-900/60 p-2 text-slate-300 hover:text-white transition-colors"
            title="Close Lightbox"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="max-w-4xl max-h-[75vh] relative overflow-hidden rounded-2xl border border-slate-800 shadow-2xl flex items-center justify-center bg-black">
            <img 
              src={activeLightboxPhoto.url} 
              alt="Lightbox display" 
              className="max-w-full max-h-[75vh] object-contain"
            />
          </div>

          <div className="mt-4 flex flex-col items-center text-center gap-2">
            <h4 className="text-sm font-bold text-white">"{activeLightboxPhoto.caption}"</h4>
            <span className="text-[10px] text-slate-400 font-semibold tracking-wide">
              Logged by {activeLightboxPhoto.uploadedByName} on {activeLightboxPhoto.date}
            </span>
            <div className="flex gap-3 mt-1">
              <button
                onClick={() => window.open(activeLightboxPhoto.url, '_blank')}
                className="flex items-center gap-1 text-xs font-bold bg-slate-900 border border-slate-800 text-slate-300 hover:text-white px-4 py-2 rounded-lg transition-colors"
              >
                <ExternalLink className="h-4 w-4" /> Open Original
              </button>
              <button
                onClick={() => triggerDownload(activeLightboxPhoto.url, 'progress_full.jpg')}
                className="flex items-center gap-1 text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-all"
              >
                <Download className="h-4 w-4" /> Download Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREMIUM PHOTO VIEWER MODAL */}
      {photoViewerData && (
        <div className="fixed inset-0 z-50 flex flex-col md:flex-row items-stretch justify-between bg-slate-955 bg-slate-950/95 backdrop-blur-md">
          {/* Main Visual Panel */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 relative bg-black">
            {/* Close Button */}
            <button
              onClick={() => {
                setPhotoViewerData(null);
                setViewerSliderIndex(0);
                setViewerZoom(1);
              }}
              className="absolute top-4 right-4 z-50 rounded-full bg-slate-900/80 p-2 text-slate-355 hover:text-white border border-slate-800 transition-colors"
              title="Close Viewer"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Slider Controls */}
            {photoViewerData.imageUrls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setViewerSliderIndex(prev => (prev === 0 ? photoViewerData.imageUrls.length - 1 : prev - 1));
                    setViewerZoom(1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-40 rounded-xl bg-slate-900/60 hover:bg-slate-900 border border-slate-800 p-3 text-white hover:scale-105 transition-all"
                  title="Previous Image"
                >
                  <ChevronRight className="h-6 w-6 rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewerSliderIndex(prev => (prev === photoViewerData.imageUrls.length - 1 ? 0 : prev + 1));
                    setViewerZoom(1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-40 rounded-xl bg-slate-900/60 hover:bg-slate-900 border border-slate-800 p-3 text-white hover:scale-105 transition-all"
                  title="Next Image"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Image Container with Zoom */}
            <div className="w-full h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden rounded-xl border border-slate-900 bg-slate-950/40 relative">
              <img
                src={photoViewerData.imageUrls[viewerSliderIndex]}
                alt="Fullscreen site progress"
                style={{ transform: `scale(${viewerZoom})`, transition: 'transform 0.2s ease-out' }}
                className="max-w-full max-h-full object-contain pointer-events-none"
              />
              
              {/* Image indicator */}
              <div className="absolute bottom-4 bg-slate-900/80 px-3 py-1 rounded-full text-slate-300 text-[10px] font-mono border border-slate-800">
                Image {viewerSliderIndex + 1} of {photoViewerData.imageUrls.length}
              </div>
            </div>

            {/* Micro-gallery layout below standard slider */}
            {photoViewerData.imageUrls.length > 1 && (
              <div className="flex gap-2.5 mt-4 overflow-x-auto max-w-full p-2 no-scrollbar">
                {photoViewerData.imageUrls.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setViewerSliderIndex(idx);
                      setViewerZoom(1);
                    }}
                    className={`h-14 w-20 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                      viewerSliderIndex === idx ? 'border-emerald-500 scale-105' : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <img src={url} alt="thumbnail" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar Metadata panel */}
          <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-slate-850 bg-slate-900 p-6 flex flex-col justify-between space-y-6">
            <div className="space-y-5">
              <div className="border-b border-slate-850 pb-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full inline-block mb-3">
                  {photoViewerData.category || photoViewerData.area || 'General Update'}
                </span>
                <h3 className="text-lg font-extrabold text-white leading-tight">
                  {photoViewerData.title}
                </h3>
                <p className="text-xs text-slate-450 mt-1">Project: {project.projectName}</p>
              </div>

              {/* Detail block */}
              <div className="space-y-4 text-xs">
                <div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase block">Contractor Caption</span>
                  <p className="text-slate-200 mt-1 italic leading-relaxed bg-slate-950/30 border border-slate-850 p-3 rounded-lg">
                    "{photoViewerData.caption}"
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-[11px] text-slate-400 pt-2 border-t border-slate-850">
                  <div>
                    <span className="text-[9px] font-bold text-slate-500 uppercase block">Upload Time</span>
                    <span className="font-mono text-slate-300">{new Date(photoViewerData.uploadedAt).toLocaleString()}</span>
                  </div>
                  {photoViewerData.priority && (
                    <div>
                      <span className="text-[9px] font-bold text-slate-500 uppercase block">Priority Level</span>
                      <span className="font-semibold text-slate-300">{photoViewerData.priority}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons Panel */}
            <div className="pt-6 border-t border-slate-850 space-y-3">
              {/* Zoom Controls */}
              <div className="flex items-center justify-between bg-slate-950/45 p-3 rounded-xl border border-slate-855 mb-2">
                <span className="text-xs text-slate-400 font-medium">Zoom Factor: <strong className="text-white font-mono">{viewerZoom.toFixed(1)}x</strong></span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setViewerZoom(prev => Math.max(1, prev - 0.5))}
                    className="h-7 w-7 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-750 text-white flex items-center justify-center font-extrabold text-xs transition-all"
                    title="Zoom Out"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewerZoom(prev => Math.min(4, prev + 0.5))}
                    className="h-7 w-7 rounded-lg bg-slate-850 hover:bg-slate-750 border border-slate-750 text-white flex items-center justify-center font-extrabold text-xs transition-all"
                    title="Zoom In"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewerZoom(1)}
                    className="px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold transition-all"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Download trigger */}
              <button
                type="button"
                onClick={() => triggerDownload(photoViewerData.imageUrls[viewerSliderIndex], `progress_viewer_${viewerSliderIndex}.jpg`)}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-750 hover:text-white border border-slate-750 py-2.5 text-xs font-bold text-slate-300 transition-all"
              >
                <Download className="h-4 w-4" /> Download Selected Photo
              </button>

              {/* Close ticket Mark as Completed trigger */}
              {photoViewerData.requestId && photoViewerData.status !== 'Completed' && (
                <button
                  type="button"
                  onClick={async () => {
                    await handleMarkRequestCompleted(photoViewerData.requestId);
                    setPhotoViewerData(prev => ({ ...prev, status: 'Completed' }));
                  }}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 py-2.5 text-xs font-bold text-white transition-all shadow-lg shadow-emerald-500/10"
                >
                  <CheckCircle className="h-4 w-4" /> Mark as Viewed & Complete Request
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setPhotoViewerData(null);
                  setViewerSliderIndex(0);
                  setViewerZoom(1);
                }}
                className="w-full rounded-lg border border-slate-850 bg-slate-950 py-2.5 text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-900 transition-all"
              >
                Close Fullscreen Viewer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
